/**
 * Live preview: the differences between the entry as the preview page was built and the form right now, as patches
 * for the page (public/preview-bridge.js applies them). Texts and pictures are shown right away; anything the
 * page can't be patched for (new or moved list items, dates, choices) is flagged, and shows after the next save.
 */
import { isVideoUrl, listItemFields, type Data, type Field } from '@hotpot/core';

export type Patch = { kind: 'text'; from: string; to: string } | { kind: 'image'; from: string; to: string; value: string };

export interface PreviewDiff {
  patches: Patch[];
  /** Some change can only be seen after saving (structure, dates, choices…). */
  needsSave: boolean;
}

export function previewDiff(fields: Field[], before: Data, after: Data): PreviewDiff {
  const out: PreviewDiff = { patches: [], needsSave: false };
  walkFields(fields, before ?? {}, after ?? {}, out);
  return out;
}

function same(a: unknown, b: unknown) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function walkFields(fields: Field[], before: Data, after: Data, out: PreviewDiff) {
  for (const f of fields) {
    const a = before[f.name];
    const b = after[f.name];
    if (same(a, b)) continue;
    walkValue(f, a, b, out);
  }
}

function walkValue(f: Field, a: unknown, b: unknown, out: PreviewDiff) {
  switch (f.type) {
    case 'string':
    case 'text':
    case 'url':
    case 'number':
      if (typeof a === 'number' || typeof b === 'number') a = a === undefined ? a : String(a);
      if (typeof a === 'string' && a.trim() && b !== undefined) {
        for (const [from, to] of lineDiff(String(a), String(b ?? ''))) out.patches.push({ kind: 'text', from, to });
      } else out.needsSave = true; // nothing on the page to find yet
      return;
    case 'markdown':
      if (typeof a === 'string' && a.trim()) {
        const pa = paragraphs(a);
        const pb = paragraphs(String(b ?? ''));
        if (pa.length !== pb.length) out.needsSave = true;
        for (let i = 0; i < Math.min(pa.length, pb.length); i++) {
          if (pa[i] !== pb[i]) out.patches.push({ kind: 'text', from: pa[i]!, to: pb[i]! });
        }
      } else out.needsSave = true;
      return;
    case 'image':
      if (typeof a === 'string' && a && typeof b === 'string' && b) out.patches.push({ kind: 'image', from: stem(a), to: '', value: b });
      else out.needsSave = true;
      return;
    case 'gallery': {
      const la = Array.isArray(a) ? (a as string[]) : [];
      const lb = Array.isArray(b) ? (b as string[]) : [];
      if (la.length !== lb.length) out.needsSave = true;
      for (let i = 0; i < Math.min(la.length, lb.length); i++) {
        // A video (a URL) can't be swapped in place in the preview; the page is rebuilt for it.
        if (la[i] !== lb[i] && (isVideoUrl(la[i]) || isVideoUrl(lb[i]))) out.needsSave = true;
        else if (la[i] !== lb[i]) out.patches.push({ kind: 'image', from: stem(la[i]!), to: '', value: lb[i]! });
      }
      return;
    }
    case 'object':
      walkFields(f.fields, (a as Data) ?? {}, (b as Data) ?? {}, out);
      return;
    case 'list': {
      const la = Array.isArray(a) ? a : [];
      const lb = Array.isArray(b) ? b : [];
      // Same items in the same order (only their contents changed) can be patched; anything else needs a save.
      const key = f.types ? f.type_key ?? 'type' : null;
      if (la.length !== lb.length || (key && la.some((x, i) => (x as Data)?.[key] !== (lb[i] as Data)?.[key]))) {
        out.needsSave = true;
        return;
      }
      for (let i = 0; i < la.length; i++) {
        if (same(la[i], lb[i])) continue;
        if (f.field) walkValue(f.field, la[i], lb[i], out);
        else {
          const itemFields = listItemFields(f, la[i]);
          if (itemFields) walkFields(itemFields, (la[i] as Data) ?? {}, (lb[i] as Data) ?? {}, out);
          else out.needsSave = true;
        }
      }
      return;
    }
    default:
      out.needsSave = true;
  }
}

/** Changed lines of a multi-line text (each line is usually its own line on the page). */
function lineDiff(a: string, b: string): [string, string][] {
  const la = a.split('\n');
  const lb = b.split('\n');
  if (la.length !== lb.length) return [[a, b]];
  return la.map((x, i) => [x, lb[i]!] as [string, string]).filter(([x, y]) => x !== y && x.trim());
}

/** Paragraphs of Markdown as the page shows them: plain text, without the markup. */
export function paragraphs(md: string): string[] {
  return md
    .replace(/\r/g, '')
    .split(/\n\s*\n|\n(?=\s*(?:[-*+]|\d+\.)\s)/)
    .map((p) =>
      p
        .replace(/^\s*(?:#{1,6}\s+|>\s?|[-*+]\s+|\d+\.\s+)/gm, '')
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/(\*\*|__|\*|_|~~|`)(.+?)\1/g, '$2')
        .replace(/\\([\\`*_{}[\]()#+\-.!])/g, '$1')
        .replace(/\s*\n\s*/g, ' ')
        .trim(),
    )
    .filter(Boolean);
}

/** File name without folders and extension: what is left of an image's name in the built page. */
export function stem(value: string): string {
  const name = value.split('/').pop() ?? value;
  return name.replace(/\.[^.]+$/, '');
}

/** What to find on the page: a picture (file stem), or a text (its start, and the start of its last paragraph). */
export type Locator = { kind: 'text'; value: string; end?: string } | { kind: 'image'; value: string };

const IMAGE_RE = /\.(jpe?g|png|gif|webp|avif|svg)$/i;

/** What to find on the page for a field's value (the first piece of text or picture in it). */
export function locatorFor(value: unknown): Locator | null {
  if (typeof value === 'string') {
    const v = value.trim();
    if (!v || /^https?:\/\//.test(v)) return null; // a video or link address isn't shown as text
    if (IMAGE_RE.test(v)) return { kind: 'image', value: stem(v) };
    const all = paragraphs(v);
    const first = all[0];
    if (!first) return null;
    const last = all.length > 1 ? all[all.length - 1]!.slice(0, 80) : undefined;
    return last ? { kind: 'text', value: first.slice(0, 80), end: last } : { kind: 'text', value: first.slice(0, 80) };
  }
  if (typeof value === 'number') return { kind: 'text', value: String(value) };
  if (Array.isArray(value)) {
    for (const item of value) {
      const l = locatorFor(item);
      if (l) return l;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      if (k === 'type') continue;
      const l = locatorFor(v);
      if (l) return l;
    }
  }
  return null;
}

/** The value at a form path such as `blocks.1.items.0.image`. */
export function valueAt(data: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((cur, k) => (cur && typeof cur === 'object' ? (cur as Record<string, unknown>)[k] : undefined), data);
}

/** From where to where something is, top and bottom (scroll positions at which they reach the reading line). */
export type Span = [number, number];

/**
 * Scroll positions that belong together in the form and on the page, from fields and the parts of the page showing
 * them (null = not on the page). A field's top and bottom go with its part's top and bottom, so while a long field
 * scrolls past, its (shorter) part of the page stays in view. Parts that are out of order on the page are left out
 * (the longest run in order is kept); parts that overlap on the page (a label next to its text, pictures side by
 * side) are joined into one. The tops and the ends belong together too.
 */
export function syncPoints(form: Span[], page: (Span | null)[], formMax: number, pageMax: number): { xs: number[]; ys: number[] } {
  const pairs = form
    .map((f, i) => ({ f, p: page[i] }))
    .filter((x): x is { f: Span; p: Span } => !!x.p)
    .sort((a, b) => a.f[0] - b.f[0]);
  // Longest run in order on both sides (side by side on the page counts as in order).
  const len = pairs.map(() => 1);
  const prev = pairs.map(() => -1);
  for (let i = 0; i < pairs.length; i++)
    for (let j = 0; j < i; j++)
      if (pairs[j]!.f[0] < pairs[i]!.f[0] && pairs[j]!.p[0] <= pairs[i]!.p[0] && len[j]! + 1 > len[i]!) {
        len[i] = len[j]! + 1;
        prev[i] = j;
      }
  let end = -1;
  len.forEach((l, i) => {
    if (end < 0 || l > len[end]!) end = i;
  });
  const run: { f: Span; p: Span }[] = [];
  for (let i = end; i >= 0; i = prev[i]!) run.unshift(pairs[i]!);
  // Join overlapping parts.
  const joined: { f: Span; p: Span }[] = [];
  for (const x of run) {
    const last = joined[joined.length - 1];
    if (last && (x.p[0] < last.p[1] || x.f[0] < last.f[1])) {
      last.f = [last.f[0], Math.max(last.f[1], x.f[1])];
      last.p = [last.p[0], Math.max(last.p[1], x.p[1])];
    } else joined.push({ f: [...x.f], p: [...x.p] });
  }
  const xs = [0];
  const ys = [0];
  const fMax = Math.max(formMax, 0);
  const pMax = Math.max(pageMax, 0);
  for (const x of joined)
    for (const k of [0, 1] as const) {
      const fx = x.f[k];
      const py = x.p[k];
      // Only positions both sides can scroll to, each further down than the one before.
      if (fx > xs[xs.length - 1]! && py > ys[ys.length - 1]! && fx < fMax && py < pMax) {
        xs.push(fx);
        ys.push(py);
      }
    }
  xs.push(fMax);
  ys.push(pMax);
  return { xs, ys };
}

/** A scroll position on one side mapped to the other, linear between the points (see syncPoints). */
export function mapScroll(x: number, xs: number[], ys: number[]): number {
  if (x <= xs[0]!) return ys[0]!;
  for (let i = 1; i < xs.length; i++) {
    if (x <= xs[i]!) {
      const span = xs[i]! - xs[i - 1]!;
      return span > 0 ? ys[i - 1]! + ((x - xs[i - 1]!) / span) * (ys[i]! - ys[i - 1]!) : ys[i]!;
    }
  }
  return ys[ys.length - 1]!;
}
